<template>
  <div v-if="hub">
    <ClientOnly>
      <AcceptTermsModal v-if="displayTermsModal" :hub="hub" />
    </ClientOnly>
    <HubHeader :breadcrumbs-items="breadcrumbsItems">
      <template v-if="slots.breadcrumbs" #breadcrumbs>
        <slot name="breadcrumbs" />
      </template>

      <div class="space-x-4">
        <Button
          v-if="shouldDisplayBackToHomeBtn"
          :label="t('hub-homepage.back-homepage')"
          :size="ButtonSize.SM"
          :variant="ButtonVariant.OUTLINED"
          @click="shouldShowHomePage = true"
        />
        <SendModal
          v-if="!hub.isContentSendingDeactivated && !isInIframe"
          :object-to-send="hub"
          :title="$t('hub.send')"
        >
          <template #object-image>
            <img
              class="aspect-square rounded-full h-53"
              :src="hub.logoUrl"
              :alt="t('content.alt')"
            />
          </template>
          <p class="text-on-background-variant font-semibold text-sm">
            {{ $t('hub.send') }}
          </p>
        </SendModal>
      </div>
    </HubHeader>
    <div v-if="shouldShowHomePage">
      <HubHomepage :hub="hub" @hide-homepage="shouldShowHomePage = false" />
    </div>
    <div v-else>
      <HubInfo :hub="hub" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useIsInIframe } from '~/composables/use-is-in-iframe';
import { computed } from 'vue';
import { Button, ButtonSize, ButtonVariant } from '@exfluential/ui';
import { useOrganizationDomain } from '#imports';
import { SendModal } from '~/pages/_components/send-modal';
import { useOrganizationStore } from '~/store/organization';
import { AcceptTermsModal } from '~/pages/_components/hub-details/_components/accept-terms-modal';
import { useAcceptedHubsTerms } from './_composables/use-accepted-hubs-terms';
import { storeToRefs } from 'pinia';
import { HubHeader } from '~/pages/_components/hub-header';
import { useI18n } from 'vue-i18n';
import { useHubStore } from '~/store/hub';
import HubHomepage from './_components/hub-homepage/HubHomepage.vue';
import { HubInfo } from './_components/hub-info';

const slots = defineSlots<{
  breadcrumbs: unknown;
}>();

const { t } = useI18n();
const isInIframe = useIsInIframe();

const organizationStore = useOrganizationStore();
const { organization } = storeToRefs(organizationStore);
const { hub } = storeToRefs(useHubStore());

useHead({
  title: hub.value?.title,
});

const shouldShowHomePage = useQueryParam('shouldShowHomePage', {
  ...booleanTransformer,
  defaultValue: hub.value?.isLaunchEnabled && hub.value?.hasHomePage,
});

const shouldDisplayBackToHomeBtn = computed<boolean>(
  () => Boolean(hub.value?.hasHomePage) && !shouldShowHomePage.value,
);

const { areHubTermsAccepted } = useAcceptedHubsTerms();

const displayTermsModal = computed(() => {
  return (
    import.meta.client &&
    hub.value?.displayTerms &&
    !areHubTermsAccepted(hub.value.id)
  );
});

const organizationDomain = useOrganizationDomain();

const breadcrumbsItems =
  organizationDomain !== null || !organization.value
    ? [
        { label: t('common.dashboard'), to: '/' },
        { label: hub.value?.title, to: `/${hub.value?.slug}` },
      ]
    : [
        { label: t('common.dashboard'), to: '/' },
        {
          label: organization.value.name,
          to: `/org/${organization.value.slug}`,
        },
        {
          label: hub.value.title,
          to: `/org/${organization.value.slug}/${hub.value.slug}`,
        },
      ];
</script>
